/**
 * The Website SEO page's one data hook, for ONE casino instance: load, debounced draft autosave, per-route live meta,
 * publish / revert as a polled job, and Lumio generation merged into the draft. Copy-free.
 *
 * Race rules: every state read takes an id from a growing counter and lands only when it is the newest read for the
 * current instance; a draft PUT answer lands only when it is the newest write for the current instance; a pending
 * write carries the instance id it was made for (an instance switch flushes it there). A re-read that fails in any
 * shape keeps the last good snapshot and sets `reloadFailed`; only a first read may show `unavailable` / `failed`.
 * A job poll that fails is not a failed job: verification can take minutes, so polling continues until the job says
 * `done` or `failed`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { creativesServiceConfigured } from '../creatives/creative-api';

import {
  getSiteSeo,
  getSiteSeoJob,
  getSiteSeoLiveMeta,
  generateSiteSeo,
  publishSiteSeo,
  revertSiteSeo,
  saveSiteSeoDraft,
  type SiteSeoResult,
} from './site-seo-api';
import {
  applyGenerated,
  applyGeneratedEmpty,
  isDirty,
  isJobOver,
  isLiveMetaPath,
  snapshotFromState,
  withDraft,
  withIssues,
  withJob,
  withLiveMeta,
  withoutLiveMeta,
  withReloaded,
  withSaved,
  type LiveMetaEntry,
  type SiteSeoSnapshot,
  type SiteSeoStatus,
} from './site-seo-state';
import type { SiteSeoDocument, SiteSeoGenerated, SiteSeoGenerateRequest, SiteSeoJob } from './seo-types';

export interface UseSiteSeoInput {
  casinoInstanceId?: string;
  debounceMs?: number;
  pollMs?: number;
}

export interface SiteSeoController {
  status: SiteSeoStatus;
  /** The last good state, kept across failed re-reads. */
  snapshot: SiteSeoSnapshot | null;
  dirty: boolean;
  /** Non-null while a publish / revert job runs; a job that ended `failed` stays here (with its `error`) until the
   *  next edit or job. */
  publishing: SiteSeoJob | null;
  /** The state's `confirmed`, and false while the last finished job said `confirmed: false` (until a read says true). */
  confirmed: boolean;
  /** The last job ended `done` with `confirmed: false`; cleared by the next good read with `confirmed: true`. */
  lastJobUnconfirmed: boolean;
  /** The last job's own final answer (`done` or `failed`), set in the same commit that ends it; null when no job ended
   *  through its poll (e.g. it vanished and a re-read showed nothing running). Cleared by the next edit or job. */
  endedJob: SiteSeoJob | null;
  /** A re-read failed after a good state; cleared by the next good read. */
  reloadFailed: boolean;
  /** The newest draft PUT for this instance failed (the edit is kept locally, not on the server); cleared by the next
   *  good PUT. `setDraft((d) => d)` re-sends the held draft. */
  saveFailed: boolean;
  setDraft: (update: (draft: SiteSeoDocument) => SiteSeoDocument) => void;
  /** Sends the pending write now; resolves when the newest write (pending or already out) is answered, ok or not,
   *  at once when there is none. */
  flushDraft: () => Promise<void>;
  openRoute: (path: string) => void;
  liveMetaOf: (path: string) => LiveMetaEntry | null;
  publish: () => Promise<SiteSeoResult<SiteSeoJob>>;
  revert: () => Promise<SiteSeoResult<SiteSeoJob>>;
  /** Lumio generation landed into the draft. `'replace'` (default) writes every returned field; `'fill-empty'` writes
   *  only into empty fields, resolves `value` with just the fields that landed, and `kept` when the service answered
   *  but none did (the owner wrote every asked field meanwhile: charged, unlike a `failed` call, which is refunded). */
  generate: (req: SiteSeoGenerateRequest, mode?: 'replace' | 'fill-empty') => Promise<SiteSeoGenerateResult>;
  reload: () => void;
}

interface HookState {
  status: SiteSeoStatus;
  snapshot: SiteSeoSnapshot | null;
  reloadFailed: boolean;
  saveFailed: boolean;
  lastJobUnconfirmed: boolean;
  /** The last job's final answer; when `failed`, shown through `publishing` until the next edit or job. */
  endedJob: SiteSeoJob | null;
}

const INITIAL: HookState = {
  status: 'idle',
  snapshot: null,
  reloadFailed: false,
  saveFailed: false,
  lastJobUnconfirmed: false,
  endedJob: null,
};

interface PendingWrite {
  casinoInstanceId: string;
  draft: SiteSeoDocument;
  timer: ReturnType<typeof setTimeout>;
}

const NOT_LOADED: SiteSeoResult<never> = { ok: false, reason: 'unavailable' };

/** A generation's outcome: the service's, plus `kept` when a fill-empty answer landed nothing (see `generate`). */
export type SiteSeoGenerateResult = SiteSeoResult<SiteSeoGenerated> | { ok: false; reason: 'kept' };

export const useSiteSeo = ({
  casinoInstanceId,
  debounceMs = 900,
  pollMs = 2500,
}: UseSiteSeoInput): SiteSeoController => {
  const enabled = Boolean(casinoInstanceId) && creativesServiceConfigured();
  const instance = enabled ? casinoInstanceId : undefined;

  const [view, setView] = useState<HookState>(INITIAL);
  // The source of truth every async landing reads; `view` is its rendered copy.
  const stateRef = useRef<HookState>(INITIAL);
  const commit = useCallback((patch: Partial<HookState>): void => {
    stateRef.current = { ...stateRef.current, ...patch };
    setView(stateRef.current);
  }, []);

  const instanceRef = useRef<string | undefined>(undefined);
  const readAbortRef = useRef<AbortController | null>(null);
  const readSeqRef = useRef(0);
  const writeSeqRef = useRef(0);
  /** Writes sent and not yet answered (any instance); a re-read keeps the local draft while one is out. */
  const writesOutRef = useRef(0);
  const pendingRef = useRef<PendingWrite | null>(null);
  /** The newest write sent, settled once answered; null when it has been answered. */
  const lastWriteRef = useRef<Promise<void> | null>(null);
  const liveSeqRef = useRef(0);
  const liveLatestRef = useRef(new Map<string, number>());
  const lastOpenedRef = useRef<string | null>(null);

  const sendDraft = useCallback(
    (id: string, draft: SiteSeoDocument): Promise<void> => {
      writeSeqRef.current += 1;
      const seq = writeSeqRef.current;
      writesOutRef.current += 1;
      // No abort signal: a write flushed on an instance switch or unmount must still reach the server.
      const written = saveSiteSeoDraft(id, draft).then((res) => {
        writesOutRef.current -= 1;
        if (lastWriteRef.current === written) lastWriteRef.current = null;
        const held = stateRef.current.snapshot;
        if (seq !== writeSeqRef.current || id !== instanceRef.current || !held) return;
        if (!res.ok) commit({ saveFailed: true });
        else commit({ snapshot: withSaved(held, res.value), saveFailed: false });
      });
      lastWriteRef.current = written;
      return written;
    },
    [commit],
  );

  const flushDraft = useCallback((): Promise<void> => {
    const pending = pendingRef.current;
    if (!pending) return lastWriteRef.current ?? Promise.resolve();
    clearTimeout(pending.timer);
    pendingRef.current = null;
    return sendDraft(pending.casinoInstanceId, pending.draft);
  }, [sendDraft]);

  /** Commits an edited snapshot and (re)arms the debounced write under the current instance. */
  const edit = useCallback(
    (next: SiteSeoSnapshot): void => {
      const id = instanceRef.current;
      if (!id) return;
      commit({ snapshot: next, endedJob: null });
      if (pendingRef.current) clearTimeout(pendingRef.current.timer);
      const timer = setTimeout(() => {
        const pending = pendingRef.current;
        if (!pending || pending.timer !== timer) return;
        pendingRef.current = null;
        void sendDraft(pending.casinoInstanceId, pending.draft);
      }, debounceMs);
      pendingRef.current = { casinoInstanceId: id, draft: next.draft, timer };
    },
    [commit, debounceMs, sendDraft],
  );

  const setDraft = useCallback(
    (update: (draft: SiteSeoDocument) => SiteSeoDocument): void => {
      const held = stateRef.current.snapshot;
      if (!held) return;
      edit(withDraft(held, update(held.draft)));
    },
    [edit],
  );

  const requestLiveMeta = useCallback(
    (path: string): void => {
      const id = instanceRef.current;
      const held = stateRef.current.snapshot;
      if (!id || !held || !isLiveMetaPath(path)) return;
      const entry = held.liveMeta[path];
      if (entry && entry.status !== 'failed') return;
      liveSeqRef.current += 1;
      const seq = liveSeqRef.current;
      liveLatestRef.current.set(path, seq);
      commit({ snapshot: withLiveMeta(held, path, { status: 'loading', value: null }) });
      void getSiteSeoLiveMeta(id, path, readAbortRef.current?.signal).then((res) => {
        const now = stateRef.current.snapshot;
        if (id !== instanceRef.current || liveLatestRef.current.get(path) !== seq || !now) return;
        const landed: LiveMetaEntry = res.ok
          ? { status: 'ready', value: res.value }
          : { status: 'failed', value: null };
        commit({ snapshot: withLiveMeta(now, path, landed) });
      });
    },
    [commit],
  );

  const openRoute = useCallback(
    (path: string): void => {
      if (!isLiveMetaPath(path)) return;
      lastOpenedRef.current = path;
      requestLiveMeta(path);
    },
    [requestLiveMeta],
  );

  /**
   * One state read for the current instance. `afterJob`: the read follows a finished job, so the server's draft wins
   * unless a local edit is still unsaved (the job changed what is published, e.g. a revert).
   */
  const read = useCallback(
    (afterJob: boolean): void => {
      const id = instanceRef.current;
      if (!id) return;
      readSeqRef.current += 1;
      const seq = readSeqRef.current;
      if (!stateRef.current.snapshot) commit({ status: 'loading' });
      void getSiteSeo(id, readAbortRef.current?.signal).then((res) => {
        if (seq !== readSeqRef.current || id !== instanceRef.current) return;
        const held = stateRef.current.snapshot;
        if (!res.ok) {
          if (held) commit({ reloadFailed: true });
          else commit({ status: res.reason === 'unavailable' ? 'unavailable' : 'failed' });
          return;
        }
        // A failed PUT is unsaved too: its edit is only here.
        const unsaved = pendingRef.current !== null || writesOutRef.current > 0 || stateRef.current.saveFailed;
        const keepDraft = held !== null && (unsaved || (!afterJob && isDirty(held)));
        const snapshot = held ? withReloaded(held, res.value, keepDraft) : snapshotFromState(res.value);
        commit({
          status: 'ready',
          snapshot,
          reloadFailed: false,
          // A read whose draft wins leaves nothing local unsaved.
          saveFailed: keepDraft ? stateRef.current.saveFailed : false,
          lastJobUnconfirmed: res.value.confirmed ? false : stateRef.current.lastJobUnconfirmed,
        });
        if (!held && lastOpenedRef.current) requestLiveMeta(lastOpenedRef.current);
      });
    },
    [commit, requestLiveMeta],
  );

  const reload = useCallback((): void => read(false), [read]);

  // Syncs with the service for one instance: the first read on a switch, and on leaving it (switch or unmount) the
  // pending write flushed under the instance it was made for and every in-flight read aborted and disowned.
  useEffect(() => {
    instanceRef.current = instance;
    const controller = new AbortController();
    readAbortRef.current = controller;
    liveLatestRef.current = new Map();
    lastOpenedRef.current = null;
    commit({ ...INITIAL, status: instance ? 'loading' : 'idle' });
    if (instance) read(false);
    return () => {
      // Not awaited: the write still reaches the server after the instance is left.
      void flushDraft();
      controller.abort();
      readSeqRef.current += 1;
      instanceRef.current = undefined;
    };
  }, [instance, commit, read, flushDraft]);

  const endJob = useCallback(
    (job: SiteSeoJob): void => {
      const held = stateRef.current.snapshot;
      if (!held) return;
      liveLatestRef.current = new Map();
      commit({
        snapshot: withoutLiveMeta(withJob(held, null)),
        endedJob: job,
        lastJobUnconfirmed: job.status === 'done' && job.confirmed === false,
      });
      read(true);
      if (lastOpenedRef.current) requestLiveMeta(lastOpenedRef.current);
    },
    [commit, read, requestLiveMeta],
  );

  const jobId = view.snapshot?.state.publishing?.id;

  // Polls the running job until it says done or failed. A failed poll (network, 5xx, 404) is not a failed job: it
  // retries on the next tick (a 404 also re-reads the state, whose `publishing` tells whether the job still runs).
  useEffect(() => {
    if (!instance || !jobId) return undefined;
    const controller = new AbortController();
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async (): Promise<void> => {
      const res = await getSiteSeoJob(instance, jobId, controller.signal);
      if (stopped || instance !== instanceRef.current) return;
      const held = stateRef.current.snapshot;
      if (!held || held.state.publishing?.id !== jobId) return;
      if (res.ok && isJobOver(res.value)) {
        endJob(res.value);
        return;
      }
      if (res.ok) commit({ snapshot: withJob(held, res.value) });
      else if (res.reason === 'unavailable') read(false);
      timer = setTimeout(() => void tick(), pollMs);
    };
    timer = setTimeout(() => void tick(), pollMs);
    return () => {
      stopped = true;
      if (timer !== undefined) clearTimeout(timer);
      controller.abort();
    };
  }, [instance, jobId, pollMs, commit, endJob, read]);

  /** Publish or revert: the service's answer as is; a job starts the poll, a refusal shows its issues and re-reads. */
  const startJob = useCallback(
    async (
      send: (id: string, draft: SiteSeoDocument) => Promise<SiteSeoResult<SiteSeoJob>>,
      carriesDraft: boolean,
    ): Promise<SiteSeoResult<SiteSeoJob>> => {
      const id = instanceRef.current;
      const held = stateRef.current.snapshot;
      if (!id || !held) return NOT_LOADED;
      // A publish carries the current draft itself: the pending write is held back, and sent only when the job is not
      // started (a refusal or failure stored nothing). A revert never carries the draft, so it sends it either way.
      // Unless a newer write was made while the call was out (sent, or armed to be): it already carries the held-back
      // edit, and the older draft must not land after it.
      const heldBack = pendingRef.current;
      if (heldBack) clearTimeout(heldBack.timer);
      pendingRef.current = null;
      commit({ endedJob: null });
      const startSeq = writeSeqRef.current;
      const res = await send(id, held.draft);
      const sentMeanwhile = writeSeqRef.current !== startSeq;
      const newer = sentMeanwhile || pendingRef.current !== null;
      if (heldBack && !newer && (!res.ok || !carriesDraft)) void sendDraft(heldBack.casinoInstanceId, heldBack.draft);
      const now = stateRef.current.snapshot;
      if (id !== instanceRef.current || !now) return res;
      if (res.ok) {
        // A started publish stores its document as the server's draft, possibly over a write sent while the call was
        // out: that edit would be lost to the re-read at the job's end, so the local draft is sent again (a write still
        // armed goes out by itself, after the call).
        const resend = carriesDraft && sentMeanwhile && pendingRef.current === null;
        // A started publish holds the draft, so an earlier failed PUT is no longer the only copy.
        commit({
          snapshot: withJob(now, res.value),
          lastJobUnconfirmed: false,
          saveFailed: carriesDraft && !resend ? false : stateRef.current.saveFailed,
        });
        if (resend) void sendDraft(id, now.draft);
      } else {
        if (res.reason === 'refused' && res.issues) commit({ snapshot: withIssues(now, res.issues) });
        read(false);
      }
      return res;
    },
    [commit, read, sendDraft],
  );

  const publish = useCallback(() => startJob((id, draft) => publishSiteSeo(id, draft), true), [startJob]);
  const revert = useCallback(() => startJob((id) => revertSiteSeo(id), false), [startJob]);

  const generate = useCallback(
    async (req: SiteSeoGenerateRequest, mode: 'replace' | 'fill-empty' = 'replace'): Promise<SiteSeoGenerateResult> => {
      const id = instanceRef.current;
      if (!id || !stateRef.current.snapshot) return NOT_LOADED;
      const res = await generateSiteSeo(id, req);
      const held = stateRef.current.snapshot;
      if (!res.ok || id !== instanceRef.current || !held) return res;
      if (mode === 'replace') {
        edit(applyGenerated(held, req, res.value));
        return res;
      }
      const { snapshot: filled, landed } = applyGeneratedEmpty(held, req, res.value);
      if (!landed.length) return { ok: false, reason: 'kept' };
      edit(filled);
      const value: SiteSeoGenerated = {};
      for (const field of landed) value[field] = res.value[field];
      return { ok: true, value };
    },
    [edit],
  );

  const { snapshot } = view;
  const liveMetaOf = useCallback((path: string): LiveMetaEntry | null => snapshot?.liveMeta[path] ?? null, [snapshot]);

  return {
    status: view.status,
    snapshot,
    dirty: snapshot ? isDirty(snapshot) : false,
    publishing: snapshot?.state.publishing ?? (view.endedJob?.status === 'failed' ? view.endedJob : null),
    confirmed: (snapshot?.state.confirmed ?? true) && !view.lastJobUnconfirmed,
    lastJobUnconfirmed: view.lastJobUnconfirmed,
    endedJob: view.endedJob,
    reloadFailed: view.reloadFailed,
    saveFailed: view.saveFailed,
    setDraft,
    flushDraft,
    openRoute,
    liveMetaOf,
    publish,
    revert,
    generate,
    reload,
  };
};
